import { memo, useCallback } from "react";
import Menu from "@mui/material/Menu";
import LogoutIcon from "components/Icons/LogoutIcon";
import { SettingMenuItemLink, SettingsMenuItem } from "..";
import useSettingsMenuItems from "../../hooks/useSettingsMenuItems";
import { useTranslation } from "react-i18next";
import { useUserContext } from "hooks";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material";
import Div from "components/primitives/Div";
import Typography from "components/Typography";

const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    minWidth: "250px",
    border: `1px solid ${theme.palette.grey[20]}`,
    borderRadius: theme.spacing(1.25),
  },

  "& .MuiList-root": {
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
  },

  "& .MuiList-root .MuiButtonBase-root.Mui-focusVisible": {
    backgroundColor: theme.palette.grey[20],
    color: theme.palette.common.black,
  },

  "& .MuiMenuItem-root .buy-more-sensors": {
    "& .MuiSvgIcon-root": {
      opacity: 0,
    },
  },

  // Revealed on keyboard focus as well as hover, so the affordance is not mouse-only.
  "& .MuiMenuItem-root:hover .buy-more-sensors, & .MuiMenuItem-root.Mui-focusVisible .buy-more-sensors": {
    "& .MuiSvgIcon-root": {
      opacity: 1,
    },
  },
}));

const anchorOrigin = { vertical: "bottom", horizontal: "right" } as const;
const transformOrigin = { vertical: "bottom", horizontal: "left" } as const;

interface SettingsMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

function SettingsMenu(props: SettingsMenuProps) {
  const { anchorEl, open, onClose } = props;

  const { t } = useTranslation();
  const settingsMenuItems = useSettingsMenuItems({ onClose });
  const [, { onSignOut }] = useUserContext();
  const navigate = useNavigate();

  const handleSignOut = useCallback(async () => {
    await onSignOut();
    navigate("/", { replace: true });
  }, [onSignOut, navigate]);

  return (
    <StyledMenu
      id="settings-menu"
      aria-labelledby="settings-button"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
    >
      {settingsMenuItems.map((item, index) =>
        item.route ? (
          <SettingMenuItemLink key={index} to={item.route} onClick={onClose} divider>
            {item.title}
          </SettingMenuItemLink>
        ) : (
          <SettingsMenuItem key={index} onClick={item.onClick} divider>
            {item.title}
          </SettingsMenuItem>
        )
      )}
      <SettingsMenuItem onClick={handleSignOut}>
        <Div sx={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", width: "100%" }}>
          <LogoutIcon fontSize="small" />
          <Typography variant="TextS">{t("settings.sign_out")}</Typography>
        </Div>
      </SettingsMenuItem>
    </StyledMenu>
  );
}

export default memo(SettingsMenu);
